import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { AuthorRow } from "../api/client";
import { PaginationControls } from "../components/PaginationControls";
import { SearchBox } from "../components/SearchBox";
import { averageViewsPerArticle, filterAuthorsByName } from "../utils/author-search";
import { DEFAULT_ARTICLE_PAGE_SIZE, clampPageIndex, paginateRows } from "../utils/pagination";

const formatter = new Intl.NumberFormat("zh-HK");
const averageFormatter = new Intl.NumberFormat("zh-HK", { maximumFractionDigits: 1 });

export function AuthorsPage({ rows }: { rows: AuthorRow[] }) {
  const [authorSearch, setAuthorSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "views", desc: true }]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_ARTICLE_PAGE_SIZE);
  const filteredRows = useMemo(() => filterAuthorsByName(rows, authorSearch), [rows, authorSearch]);
  const columns = useMemo<ColumnDef<AuthorRow>[]>(
    () => [
      { accessorKey: "author", header: "作者" },
      {
        accessorKey: "article_count",
        header: "文章數",
        cell: ({ row }) => formatter.format(row.original.article_count)
      },
      {
        id: "average_views",
        accessorFn: averageViewsPerArticle,
        header: "每篇文章平均瀏覽量",
        cell: ({ row }) => averageFormatter.format(averageViewsPerArticle(row.original))
      },
      {
        accessorKey: "views",
        header: "總觀看",
        cell: ({ row }) => formatter.format(row.original.views)
      },
      {
        accessorKey: "member_views",
        header: "會員文章觀看",
        cell: ({ row }) => formatter.format(row.original.member_views)
      },
      {
        accessorKey: "public_views",
        header: "一般文章觀看",
        cell: ({ row }) => formatter.format(row.original.public_views)
      }
    ],
    []
  );
  const table = useReactTable({
    data: filteredRows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiSort: false,
    enableSortingRemoval: true,
    sortDescFirst: false
  });
  const sortedRows = table.getSortedRowModel().rows;
  const page = paginateRows(sortedRows, pageIndex, pageSize);

  useEffect(() => {
    setPageIndex(0);
  }, [rows, authorSearch, pageSize, sorting]);

  useEffect(() => {
    setPageIndex((current) => clampPageIndex(current, sortedRows.length, pageSize));
  }, [sortedRows.length, pageSize]);

  const renderPaginationControls = () => (
    <PaginationControls
      pageIndex={page.pageIndex}
      pageSize={page.pageSize}
      totalRows={sortedRows.length}
      onPageIndexChange={setPageIndex}
      onPageSizeChange={setPageSize}
    />
  );

  return (
    <div className="page-stack">
      <div className="toolbar-row">
        <h2>作者排行</h2>
        <SearchBox
          value={authorSearch}
          label="搜尋作者"
          placeholder="搜尋作者姓名"
          clearTitle="清除作者搜尋"
          onChange={setAuthorSearch}
        />
      </div>
      <section className="panel">
        <div className="table-stack">
          {renderPaginationControls()}
          <div className="table-wrap">
            <table>
              <thead>
                {table.getHeaderGroups().map((group) => (
                  <tr key={group.id}>
                    {group.headers.map((header) => {
                      const direction = header.column.getIsSorted();
                      const ariaSort = direction === "asc" ? "ascending" : direction === "desc" ? "descending" : "none";
                      const nextDirection = header.column.getNextSortingOrder();
                      const sortTitle = nextDirection === "asc" ? "按升冪排列" : nextDirection === "desc" ? "按降冪排列" : "取消排序";

                      return (
                        <th key={header.id} aria-sort={ariaSort}>
                          <button
                            type="button"
                            title={sortTitle}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {direction === "asc" ? (
                              <ArrowUp size={13} aria-hidden="true" />
                            ) : direction === "desc" ? (
                              <ArrowDown size={13} aria-hidden="true" />
                            ) : (
                              <ArrowUpDown size={13} aria-hidden="true" />
                            )}
                          </button>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {page.items.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {renderPaginationControls()}
        </div>
      </section>
    </div>
  );
}
